import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Bell,
  Calendar,
  CheckCircle2,
  CreditCard,
  Loader2,
  Mail,
  PlayCircle,
  Sparkles,
  Trash2,
  User as UserIcon,
  X,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { apiClient } from "../lib/api";
import { useAuth, startLogin } from "../lib/auth";

const wc = { withCredentials: true };

function SectionCard({ title, kicker, action, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="border border-black bg-white p-6 md:p-8"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-1">
            {kicker}
          </div>
          <h2 className="font-display text-2xl tracking-tight">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

// =========================== ACCOUNT INFO ===========================
function AccountCard({ summary, onChange }) {
  const { user } = summary;
  return (
    <SectionCard
      kicker="/ Account"
      title="Your details"
      testId="profile-account-card"
    >
      <div className="flex items-center gap-4">
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name || user.email}
            className="w-16 h-16 border border-black"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-16 h-16 bg-[#0047FF] text-white grid place-items-center font-display text-2xl">
            {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-display text-xl">{user.name || user.email}</div>
          <div className="font-mono text-xs text-[#475569] truncate">
            {user.email}
          </div>
          {user.is_admin && (
            <span className="inline-block mt-1 px-1.5 py-0.5 bg-[#0A0A0A] text-white font-mono text-[10px] uppercase tracking-widest">
              Admin
            </span>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mt-6 border-t border-[#E2E8F0] pt-6">
        <Stat label="Scans" value={summary.stats.scans} />
        <Stat label="Pro Unlocks" value={summary.stats.pro_unlocks} />
        <Stat label="Monitored Sites" value={summary.stats.monitored_sites} />
      </div>
    </SectionCard>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#E2E8F0] p-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
        {label}
      </div>
      <div className="font-display text-2xl">{value}</div>
    </div>
  );
}

// =========================== SUBSCRIPTION ===========================
function SubscriptionCard({ summary, reload }) {
  const navigate = useNavigate();
  const sub = summary.subscription || {};
  const [starting, setStarting] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const start = async () => {
    setStarting(true);
    try {
      const res = await apiClient.post(
        "/subscription/checkout",
        { origin_url: window.location.origin },
        wc,
      );
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start subscription");
    } finally {
      setStarting(false);
    }
  };

  const cancel = async () => {
    if (
      !window.confirm(
        "Cancel your monitoring subscription at the end of the current period?",
      )
    )
      return;
    setCanceling(true);
    try {
      await apiClient.post("/subscription/cancel", null, wc);
      toast.success("Subscription will end at period close");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setCanceling(false);
    }
  };

  if (!sub.active) {
    return (
      <SectionCard
        kicker="/ Subscription"
        title="Pro Monitoring · $19/mo"
        testId="profile-subscription-card"
      >
        <p className="text-[#475569] text-sm mb-5">
          Unlock automated daily/weekly site scans with email alerts when your
          TrustScore drops or new HIGH-severity issues appear.
        </p>
        <ul className="text-sm space-y-2 mb-6">
          {[
            "Daily or weekly monitoring of any owned scan",
            "Email alerts on score drops & new HIGH issues",
            "Full scan history per monitored site",
            "Cancel anytime",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#10B981] flex-shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <button
            onClick={start}
            disabled={starting}
            data-testid="start-subscription-btn"
            className="pl-btn-primary px-5 py-2.5 text-sm border border-black inline-flex items-center gap-2 disabled:opacity-60"
          >
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Start subscription
          </button>
          <Link
            to="/pricing"
            className="px-5 py-2.5 text-sm border border-[#0A0A0A] hover:bg-[#F1F5F9]"
          >
            See full pricing
          </Link>
        </div>
      </SectionCard>
    );
  }

  const endsAt = sub.current_period_end
    ? new Date(sub.current_period_end).toLocaleDateString()
    : "—";
  return (
    <SectionCard
      kicker="/ Subscription"
      title="Pro Monitoring · Active"
      testId="profile-subscription-card"
      action={
        <span
          className="px-2 py-1 bg-[#10B981] text-white font-mono text-[10px] uppercase tracking-widest"
          data-testid="sub-active-badge"
        >
          Active
        </span>
      }
    >
      <div className="grid sm:grid-cols-2 gap-3">
        <Stat label="Status" value={sub.status || "active"} />
        <Stat
          label={sub.cancel_at_period_end ? "Ends" : "Renews"}
          value={endsAt}
        />
      </div>
      {sub.cancel_at_period_end ? (
        <div className="mt-5 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 inline mr-1" />
          Scheduled to cancel at period end. Monitoring stops on {endsAt}.
        </div>
      ) : (
        <button
          onClick={cancel}
          disabled={canceling}
          data-testid="cancel-subscription-btn"
          className="mt-5 px-4 py-2 text-sm border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
        >
          {canceling ? <Loader2 className="w-4 h-4 animate-spin inline" /> : null} Cancel subscription
        </button>
      )}
    </SectionCard>
  );
}

// =========================== MONITORED SITES ===========================
function MonitoringCard({ summary, reload }) {
  const navigate = useNavigate();
  const [sites, setSites] = useState(null);
  const [scans, setScans] = useState([]);
  const [addingFor, setAddingFor] = useState(null);
  const [adding, setAdding] = useState(false);
  const [frequency, setFrequency] = useState("weekly");
  const [busy, setBusy] = useState(null);

  const isSubscribed = summary.subscription?.active;

  const loadSites = async () => {
    try {
      const r = await apiClient.get("/monitoring/sites", wc);
      setSites(r.data.sites || []);
    } catch {
      setSites([]);
    }
  };
  const loadScans = async () => {
    try {
      const r = await apiClient.get("/dashboard/scans", wc);
      setScans(r.data.scans || []);
    } catch {
      setScans([]);
    }
  };

  useEffect(() => {
    loadSites();
    loadScans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monitoredDomains = useMemo(
    () => new Set((sites || []).map((s) => s.domain)),
    [sites],
  );
  const eligibleScans = useMemo(
    () =>
      (scans || []).filter(
        (s) =>
          s.status === "complete" &&
          s.domain &&
          !monitoredDomains.has(s.domain),
      ),
    [scans, monitoredDomains],
  );

  const submitAdd = async (scan) => {
    setAdding(true);
    try {
      await apiClient.post(
        "/monitoring/sites",
        {
          scan_id: scan.scan_id,
          frequency,
          alert_on_score_drop: true,
          alert_on_new_high_issue: true,
          score_drop_threshold: 5,
        },
        wc,
      );
      toast.success(`Monitoring ${scan.domain}`);
      setAddingFor(null);
      loadSites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add monitor");
    } finally {
      setAdding(false);
    }
  };

  const removeSite = async (domain) => {
    if (!window.confirm(`Stop monitoring ${domain}?`)) return;
    setBusy(domain + "del");
    try {
      await apiClient.delete(`/monitoring/sites/${domain}`, wc);
      toast.success(`Stopped monitoring ${domain}`);
      loadSites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    } finally {
      setBusy(null);
    }
  };

  const toggleSetting = async (domain, patch) => {
    setBusy(domain + "edit");
    try {
      await apiClient.put(`/monitoring/sites/${domain}`, patch, wc);
      loadSites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  const runNow = async (domain) => {
    setBusy(domain + "run");
    try {
      const r = await apiClient.post(`/monitoring/sites/${domain}/run`, null, wc);
      if (r.data?.ok) {
        toast.success(
          `Re-scanned · score ${r.data.trust_score} (${
            r.data.score_delta >= 0 ? "+" : ""
          }${r.data.score_delta})${r.data.alert_sent ? " · alert sent" : ""}`,
        );
        loadSites();
      } else {
        toast.error("Scan didn't complete cleanly");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setBusy(null);
    }
  };

  if (!isSubscribed) {
    return (
      <SectionCard
        kicker="/ Monitoring"
        title="Automated change alerts"
        testId="profile-monitoring-card"
      >
        <div className="border border-dashed border-[#94A3B8] bg-[#F8F9FA] p-6 text-center">
          <Sparkles className="w-6 h-6 inline text-[#0047FF] mb-2" />
          <p className="text-sm text-[#475569]">
            Monitoring becomes available with the <strong>$19/mo Pro
            Monitoring</strong> subscription.
          </p>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      kicker="/ Monitoring"
      title="Monitored sites"
      testId="profile-monitoring-card"
      action={
        eligibleScans.length ? (
          <button
            onClick={() => setAddingFor("pick")}
            data-testid="add-monitor-btn"
            className="pl-btn-primary px-4 py-2 text-sm border border-black inline-flex items-center gap-2"
          >
            <PlayCircle className="w-4 h-4" /> Add a site
          </button>
        ) : null
      }
    >
      {sites === null ? (
        <div className="flex items-center gap-2 text-sm text-[#475569]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : sites.length === 0 ? (
        <div className="border border-dashed border-[#94A3B8] bg-[#F8F9FA] p-6 text-center text-sm text-[#475569]">
          You aren&apos;t monitoring any sites yet.
          {eligibleScans.length === 0 && (
            <div className="mt-2">
              Run a scan first → <Link to="/" className="text-[#0047FF] underline">Scan a site</Link>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {sites.map((s) => (
            <div
              key={s.domain}
              className="border border-[#E2E8F0] p-4"
              data-testid={`monitored-${s.domain}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <Link
                    to={`/report/${s.domain}`}
                    className="font-display text-lg hover:text-[#0047FF]"
                  >
                    {s.domain}
                  </Link>
                  <div className="font-mono text-xs text-[#475569] mt-0.5">
                    Last run:{" "}
                    {s.last_run_at
                      ? new Date(s.last_run_at).toLocaleString()
                      : "—"}{" "}
                    · Next:{" "}
                    {s.next_run_at
                      ? new Date(s.next_run_at).toLocaleString()
                      : "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => runNow(s.domain)}
                    disabled={busy === s.domain + "run"}
                    className="px-2 py-1 text-xs border border-black hover:bg-[#0047FF] hover:text-white disabled:opacity-50"
                    data-testid={`run-now-${s.domain}`}
                    title="Run scan now"
                  >
                    {busy === s.domain + "run" ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <PlayCircle className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <button
                    onClick={() => removeSite(s.domain)}
                    disabled={busy === s.domain + "del"}
                    className="px-2 py-1 text-xs border border-black hover:bg-red-500 hover:text-white"
                    data-testid={`remove-${s.domain}`}
                    title="Stop monitoring"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 mt-3 text-sm">
                <div className="border border-[#E2E8F0] px-3 py-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
                    Frequency
                  </div>
                  <select
                    value={s.frequency}
                    onChange={(e) =>
                      toggleSetting(s.domain, { frequency: e.target.value })
                    }
                    data-testid={`freq-${s.domain}`}
                    className="mt-1 bg-transparent outline-none w-full font-medium"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
                <div className="border border-[#E2E8F0] px-3 py-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
                    Baseline score
                  </div>
                  <div className="mt-1 font-display text-xl">
                    {s.baseline_trust_score ?? "—"}
                  </div>
                </div>
                <div className="border border-[#E2E8F0] px-3 py-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
                    Last alert
                  </div>
                  <div className="mt-1 text-sm">
                    {s.last_alert_at
                      ? new Date(s.last_alert_at).toLocaleString()
                      : "—"}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 mt-3 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!s.alert_on_score_drop}
                    onChange={(e) =>
                      toggleSetting(s.domain, {
                        alert_on_score_drop: e.target.checked,
                      })
                    }
                    data-testid={`alert-score-${s.domain}`}
                  />
                  Alert on score drop ≥ {s.score_drop_threshold ?? 5}
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!s.alert_on_new_high_issue}
                    onChange={(e) =>
                      toggleSetting(s.domain, {
                        alert_on_new_high_issue: e.target.checked,
                      })
                    }
                    data-testid={`alert-high-${s.domain}`}
                  />
                  Alert on new HIGH issue
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={s.enabled !== false}
                    onChange={(e) =>
                      toggleSetting(s.domain, { enabled: e.target.checked })
                    }
                    data-testid={`enabled-${s.domain}`}
                  />
                  Enabled
                </label>
              </div>

              {Array.isArray(s.history) && s.history.length > 0 && (
                <details className="mt-3 text-xs text-[#475569]">
                  <summary className="cursor-pointer font-mono uppercase tracking-widest">
                    Recent runs ({s.history.length})
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {s.history.slice(-10).reverse().map((h, i) => (
                      <li key={i} className="font-mono">
                        {new Date(h.ran_at).toLocaleString()} · score{" "}
                        {h.trust_score} ({h.score_delta >= 0 ? "+" : ""}
                        {h.score_delta})
                        {h.alert_sent ? (
                          <span className="ml-2 text-amber-700">
                            alert sent
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {addingFor === "pick" && (
        <Modal onClose={() => setAddingFor(null)} title="Add a site to monitor">
          {eligibleScans.length === 0 ? (
            <p className="text-sm text-[#475569]">
              No eligible scans available. Run a free scan first, then come
              back.
            </p>
          ) : (
            <>
              <p className="text-sm text-[#475569] mb-4">
                Pick a completed scan you own. We&apos;ll re-scan it on the schedule
                you choose and email you when something changes.
              </p>
              <div className="mb-4">
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
                  Frequency
                </label>
                <div className="flex gap-2 mt-2">
                  {["daily", "weekly"].map((f) => (
                    <label
                      key={f}
                      className={`flex-1 border px-3 py-2 cursor-pointer text-center ${
                        frequency === f
                          ? "bg-black text-white border-black"
                          : "border-black"
                      }`}
                    >
                      <input
                        type="radio"
                        className="hidden"
                        checked={frequency === f}
                        onChange={() => setFrequency(f)}
                        data-testid={`new-freq-${f}`}
                      />
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
              <div className="border border-[#E2E8F0] max-h-72 overflow-y-auto">
                {eligibleScans.map((scan) => (
                  <button
                    key={scan.scan_id}
                    onClick={() => submitAdd(scan)}
                    disabled={adding}
                    data-testid={`pick-scan-${scan.domain}`}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left border-b border-[#E2E8F0] hover:bg-[#F1F5F9] disabled:opacity-60"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{scan.domain}</div>
                      <div className="text-xs text-[#475569] font-mono">
                        score {scan.trust_score} ·{" "}
                        {new Date(scan.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    {adding ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <PlayCircle className="w-5 h-5 text-[#0047FF]" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </Modal>
      )}
    </SectionCard>
  );
}

// =========================== DANGER ZONE ===========================
function DangerZone({ refresh }) {
  const [open, setOpen] = useState(false);
  const [deleteScans, setDeleteScans] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const confirm = async () => {
    if (confirmText.toLowerCase() !== "delete my account") {
      toast.error('Type "delete my account" to confirm');
      return;
    }
    setDeleting(true);
    try {
      await apiClient.delete(
        `/profile/account?delete_scans=${deleteScans ? "true" : "false"}`,
        wc,
      );
      toast.success("Account deleted");
      // Drop local auth state and bounce home
      try {
        await logout();
      } catch {
        // logout best-effort
      }
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      setDeleting(false);
    }
  };

  return (
    <section
      data-testid="profile-danger-zone"
      className="border border-red-500 bg-red-50 p-6 md:p-8"
    >
      <div className="font-mono text-[11px] uppercase tracking-widest text-red-700 mb-1">
        / Danger Zone
      </div>
      <h2 className="font-display text-2xl tracking-tight">
        Delete your account
      </h2>
      <p className="text-sm text-red-900 mt-2">
        Permanently remove your PrivacyLens account. You&apos;ll be signed out and
        cannot undo this. Per GDPR Article 17 (right to erasure) we&apos;ll cancel
        any active subscription, delete your sessions and monitoring data, and
        either anonymise or fully erase the scans you&apos;ve created — your choice.
      </p>
      <button
        onClick={() => setOpen(true)}
        data-testid="open-delete-modal-btn"
        className="mt-5 px-4 py-2 text-sm border border-red-500 text-red-700 hover:bg-red-500 hover:text-white inline-flex items-center gap-2"
      >
        <Trash2 className="w-4 h-4" /> Delete my account
      </button>

      {open && (
        <Modal
          onClose={() => setOpen(false)}
          title="Confirm account deletion"
          testId="delete-account-modal"
        >
          <p className="text-sm text-[#475569]">
            This action is <strong>permanent</strong>. We&apos;ll cancel your
            subscription (if any), wipe sessions, monitoring rules, and Pro
            access. Pick how to treat your scan history:
          </p>
          <label className="flex items-start gap-2 mt-4 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={deleteScans}
              onChange={(e) => setDeleteScans(e.target.checked)}
              className="mt-1"
              data-testid="delete-scans-checkbox"
            />
            <div>
              <div className="font-medium">
                Also delete all public scan reports I&apos;ve created
              </div>
              <div className="text-xs text-[#475569]">
                If unchecked, scans stay published but are anonymised (no email
                / no link to you). Recommended for SEO continuity if you&apos;ve
                shared report URLs.
              </div>
            </div>
          </label>
          <div className="mt-5">
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#475569] block mb-1">
              Type <strong>delete my account</strong> to confirm
            </label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="delete-confirm-input"
              className="w-full border border-black px-3 py-2 outline-none focus:bg-[#FEF2F2]"
              placeholder="delete my account"
            />
          </div>
          <div className="flex gap-2 mt-5">
            <button
              onClick={confirm}
              disabled={
                deleting || confirmText.toLowerCase() !== "delete my account"
              }
              data-testid="confirm-delete-btn"
              className="px-4 py-2 text-sm bg-red-600 text-white border border-red-700 hover:bg-red-700 inline-flex items-center gap-2 disabled:opacity-50"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Permanently delete
            </button>
            <button
              onClick={() => setOpen(false)}
              data-testid="cancel-delete-btn"
              className="px-4 py-2 text-sm border border-[#0A0A0A] hover:bg-[#F1F5F9]"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}

// =========================== MODAL ===========================
function Modal({ onClose, title, children, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      data-testid={testId}
    >
      <div
        className="bg-white border border-black w-full max-w-[520px] p-6 md:p-8 pl-hard-shadow-sm relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 hover:bg-[#F1F5F9]"
          data-testid="modal-close"
        >
          <X className="w-4 h-4" />
        </button>
        {title && (
          <h3 className="font-display text-2xl tracking-tight mb-4 pr-6">
            {title}
          </h3>
        )}
        {children}
      </div>
    </div>
  );
}

// =========================== PAGE ===========================
export default function Profile() {
  const { user, loading } = useAuth();
  const [summary, setSummary] = useState(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      startLogin("/profile");
    }
  }, [user, loading]);

  const load = async () => {
    try {
      const r = await apiClient.get("/profile/summary", wc);
      setSummary(r.data);
    } catch {
      // ignore – likely 401, the redirect above will fire
    }
  };
  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Stripe success bounce — confirm subscription took
  useEffect(() => {
    if (params.get("subscribed") === "1") {
      toast.success("Subscription active 🎉");
      // Poll briefly to let webhook write the subscription doc
      let tries = 0;
      const t = setInterval(async () => {
        try {
          const r = await apiClient.get("/profile/summary", wc);
          setSummary(r.data);
          if (r.data?.subscription?.active || tries >= 6) clearInterval(t);
        } catch {
          // swallow — webhook may not have fired yet
        }
        tries += 1;
      }, 2000);
      return () => clearInterval(t);
    }
  }, [params]);

  if (loading || !user) {
    return (
      <main className="max-w-[1100px] mx-auto px-6 lg:px-12 py-20">
        <Loader2 className="w-5 h-5 animate-spin text-[#0047FF]" />
      </main>
    );
  }

  return (
    <main data-testid="profile-page" className="border-b border-black">
      <section className="border-b border-black bg-[#0A0A0A] text-white">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-10">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-2">
            <UserIcon className="w-4 h-4" /> Profile
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight">
            Your PrivacyLens account
          </h1>
          <p className="text-[#94A3B8] mt-2 text-sm">
            Manage your subscription, monitoring, and account data.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F9FA]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-10 space-y-6">
          {!summary ? (
            <div className="flex items-center gap-2 text-sm text-[#475569]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading profile…
            </div>
          ) : (
            <>
              <AccountCard summary={summary} onChange={load} />
              <SubscriptionCard summary={summary} reload={load} />
              <MonitoringCard summary={summary} reload={load} />
              <DangerZone refresh={load} />
            </>
          )}
        </div>
      </section>
    </main>
  );
}
